import {createSelector} from 'reselect'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'

import DatePicker from '../../../common/components/DatePicker.js'
import {
  formsSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import className from '../../../common/className.js'
import {createNewTask} from '../../../data/tasks.js'

export const MODAL_FORM = 'export_usps_transactions_modal'

export function showExportUSPSTransactionsModal() {
  setForm(MODAL_FORM, {
    startDate: null,
    errorDate: null,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export const errorsSelector = createSelector(
  modalFormSelector,
  ({startDate, endDate}) => {
    const errors = {}

    if (startDate && endDate && startDate > endDate) {
      errors.startDate = 'Date must be before end date'
      errors.preventSave = true
    }

    return errors
  },
)

export function exportUSPSTransactionsTaskParamsSelector(state) {
  const {startDate, endDate} = modalFormSelector(state)

  const task = {
    type: 'export_postage_account',
    params: {
      ...(startDate
        ? {
            start_date: startOfDay(startDate).toISOString(),
          }
        : {}),
      ...(endDate
        ? {
            end_date: endOfDay(endDate).toISOString(),
          }
        : {}),
    },
  }

  return task
}

export async function exportUSPSTransactions() {
  try {
    const params = exportUSPSTransactionsTaskParamsSelector(getState())

    updateModalForm({
      isRequesting: true,
      serverError: null,
    })

    await createNewTask(params.type, params.params)

    closeModal()

    showMessageToast(
      'A CSV file is being generated and can be downloaded from the activity menu.',
    )
  } catch (err) {
    updateModalForm({
      serverError: `Error creating export task: ${
        err.error_message || err.message
      }`,
      isRequesting: false,
    })
  }
}

function ExportUSPSTransactionsModal({form}) {
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      title="USPS Transaction History"
      onConfirm={() => exportUSPSTransactions()}
      onCancel={() => closeModal()}
      confirmText="Export CSV"
      cancelText="Cancel"
      isSaving={form.isRequesting}
      isDisabled={errors.preventSave}
      error={form.serverError}
      preventInnerScroll
    >
      <ul className="list">
        <li className="list__item--form list__item--no-style margin-bottom-25">
          Enter a specific date range or leave the fields blank to export the
          last 30 days of transactions.
        </li>
        <li className="list__item--form list__item--no-style flex">
          <div className={className`${{error: errors.startDate}}`}>
            <label className="clearfix" htmlFor="id_start_date">
              <span className="left">Start Date</span>
            </label>
            <DatePicker
              id="id_start_date"
              onChange={(date) =>
                updateModalForm({
                  startDate: date,
                })
              }
              selected={form.startDate}
              startDate={form.startDate}
              endDate={form.endDate}
            />
            {errors.startDate && (
              <small className="error error-message">{errors.startDate}</small>
            )}
          </div>
          <div className={className`margin-left-15 ${{error: errors.endDate}}`}>
            <label className="clearfix" htmlFor="id_end_date">
              <span className="left">End Date</span>
            </label>
            <DatePicker
              id="id_end_date"
              onChange={(date) =>
                updateModalForm({
                  endDate: date,
                })
              }
              selected={form.endDate}
              startDate={form.startDate}
              endDate={form.endDate}
            />
            {errors.endDate && (
              <small className="error error-message">{errors.endDate}</small>
            )}
          </div>
        </li>
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(ExportUSPSTransactionsModal, modalFormSelector)
